import axios from 'axios';
import { AttributionReport } from '../lib/attribution';
import { flattenApiErrorBody } from '../lib/apiErrorBody';
import { trackBeginCheckout } from '../lib/checkoutConversion';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Pricing-gate refusals carry `error: {type, message}`; every call site
    // renders `data.error`, so it must be a string by the time they see it
    // (React #31 crash, staging 2026-10-08). See lib/apiErrorBody.ts.
    if (error && error.response && error.response.data) flattenApiErrorBody(error.response.data);
    if (error.response?.status === 401) {
      // Exclude GitHub-related API calls from the global 401 handler
      // as they have their own refresh logic on the backend.
      const isGithubApiCall = error.config.url.includes('/github') || error.config.url.includes('/branches');
      if (!isGithubApiCall) {
        // Token expired or invalid for our own backend
        localStorage.removeItem('authToken');
        window.location.href = '/auth';
      }
    }
    // Waitlist gate: a gated user who hits a protected API directly (or whose
    // access lapsed) gets 403 { error: 'access_required' }. Send them to the
    // wall — EXCEPT on pages where the gate must not interrupt the user:
    //   /access     — redirect loop guard (and /waitlist, its old address).
    //   /onboarding — onboarding MUST complete first so we capture the user's
    //     profile data before they hit the wall. A gated, not-yet-onboarded
    //     signup triggers background gated calls (e.g. ProjectProvider's eager
    //     fetchProjects on mount); without this guard that 403 would yank them
    //     off onboarding straight to /access. This same interceptor still
    //     sends them to the wall the proper way once onboarding is done.
    const gateExemptPath =
      window.location.pathname === '/access' ||
      window.location.pathname === '/waitlist' ||
      window.location.pathname === '/onboarding' ||
      window.location.pathname.startsWith('/auth');
    if (
      error.response?.status === 403 &&
      error.response?.data?.error === 'access_required' &&
      !gateExemptPath
    ) {
      window.location.href = '/access';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (name: string, email: string, password: string) =>
    api.post('/auth/register', { name, email, password }),
  
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  
  me: () => api.get('/auth/me'),

  // The one-time code from the OAuth redirect → the session token (AuthCallback).
  exchangeHandoff: (code: string) => api.post<{ token: string }>('/auth/exchange', { code }),
  
  updateProfile: (data: any) => api.put('/auth/profile', data),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  
  logout: () => api.post('/auth/logout'),
  
  connectGithub: () => api.post('/auth/connect-github'),
  
  disconnectGithub: () => api.post('/auth/disconnect-github'),
  
  completeOnboarding: (data: {
    firstName: string;
    lastName: string;
    companyName?: string;
    companySize: string;
    role: string;
    roleOther?: string;
    industry: string;
    industryOther?: string;
    intent: string;
    replaceTargets?: string[];
    replaceCustom?: string[];
    replaceSearches?: string[];
  }) => api.post('/auth/complete-onboarding', data),

  // Save-as-you-go companion to completeOnboarding above — called on every
  // Continue/Skip press so a partial answer survives a closed tab or a lost
  // connection. Every field is optional; the server only writes what's sent
  // and never completes onboarding from this route (see PATCH
  // /auth/onboarding-progress in server/routes/auth.js).
  saveOnboardingProgress: (
    data: Partial<{
      firstName: string;
      lastName: string;
      companyName: string;
      companySize: string;
      role: string;
      roleOther: string;
      industry: string;
      industryOther: string;
      intent: string;
      replaceTargets: string[];
      replaceCustom: string[];
      replaceSearches: string[];
    }> & { step: string }
  ) => api.patch('/auth/onboarding-progress', data),

  // Waitlist gating
  waitlistInfo: () => api.get<{ salesBookingUrl: string }>('/auth/waitlist/info'),
  waitlistVisited: () => api.post('/auth/waitlist/visited'),
  waitlistBookedCall: () => api.post('/auth/waitlist/booked-call'),
  // Kalendar reported a booking made inside the app (BookCallModal), labelled
  // with the button that started it. Pure telemetry — never awaited for UI.
  bookingCompleted: (data: { cta_source: string; event_type: string }) =>
    api.post('/auth/booking-completed', data),
  redeemAccessCode: (code: string) =>
    api.post<{ allowed: boolean }>('/auth/redeem-access-code', { code }),
  // Signup invite link (?invite=<code>) — see lib/signupInvite.ts.
  redeemInvite: (code: string) =>
    api.post<{ label: string; granted: boolean; alreadyRedeemed: boolean; allowed: boolean; credit: { added: boolean; reason: string | null } | null }>(
      '/auth/redeem-invite',
      { code }
    ),

  // Guided tour v2 persistence — see lib/coachJourney.ts. Bare `auth`, not
  // gated behind requireAppAccess.
  setTourState: (body: { tour: string; status: 'completed' | 'dismissed'; version: number; lastStepIndex?: number }) =>
    api.put('/auth/tours', body),

  recordJourneyMilestone: (milestone: string) => api.put('/auth/journey', { milestone }),
};

/**
 * GET /projects/:id/dev-server — the dev-server status poll (every 2-5s while a
 * task page is open).
 *
 * Typed explicitly because this response is where `previewUrl` travels, and
 * ONLY here: it is deliberately absent from the projects-list summary and from
 * the task-detail populate string, both of which are hand-written server-side
 * field lists. A field missing from one of those arrives as `undefined` with no
 * error anywhere, which has silently disabled a client gate six times over.
 * Declaring the shape means a rename or removal on the server is a compile
 * error here rather than a feature that quietly stops working — the failure
 * mode an `as any` cast on the read has already hidden once.
 */
export interface DevServerStatusResponse {
  /** ALWAYS the fly.dev address. Never repointed — health checks, thumbnail
   *  capture and the agent daemon depend on it. */
  url: string | null;
  /** The branded `<label>.<preview domain>` address, when the preview feature is
   *  on for this project AND the edge is currently healthy. ABSENT (not null)
   *  otherwise, so `previewUrl ?? url` is the whole rollback path. */
  previewUrl?: string;
  port?: number | null;
  status: string;
  logs?: string[];
  error?: string | null;
  idleWarning?: boolean;
  snapshotUrl?: string | null;
  snapshotAt?: string | null;
}

// Attribution — belt-and-braces persistence of the shared cross-repo cookie
// contract onto the User doc (cookies alone don't survive Safari ITP's 7-day
// cap on script-set cookies, or a device switch). Fire-and-forget from the
// caller's side; see pages/AuthCallback.tsx.
export const attributionAPI = {
  report: (payload: AttributionReport) => api.post('/attribution', payload),
};

// Projects API
export const projectsAPI = {
  getAll: () => api.get('/projects'),
  
  getById: (id: string) => api.get(`/projects/${id}`),
  
  create: (data: {
    repoUrl: string;
    name?: string;
    description?: string;
    /** Import at a branch other than GitHub's default. Stored as the
     *  project's defaultBranch, which is what every downstream read
     *  (clone, sync, dev-server boot, worker fallback) goes by. */
    branch?: string;
    baseUrl?: string;
    deploymentType?: 'none' | 'amplify' | 'github_actions';
    deploymentWorkflowFile?: string;
    deploymentTimeoutMinutes?: number;
    environmentVariables?: Array<{ key: string; value: string; isSecret: boolean }>;
    /** The app picked in the import check. A request: the server honours it
     *  only where its own probe found an app. */
    mobileAppDir?: string | null;
  }) =>
    api.post('/projects', data),

  /** What importing this repo will do (the import panel). Reads GitHub only;
   *  { enabled: false } while the server's import check is switched off. */
  importCheck: (data: { repoUrl: string; branch?: string; pickedDir?: string | null }) =>
    api.post('/projects/import-check', data),
  /** Change which phone app this project shows (the "App shown" row). 409
   *  { typeChange } when the pick is a website or the project is one. */
  setMobileApp: (id: string, pickedDir: string) =>
    api.put(`/projects/${id}/mobile-app`, { pickedDir }),
  /** Can Expo Go open this phone app? compatible null = unknown. */
  expoGo: (id: string) => api.get(`/projects/${id}/mobile/expo-go`),
  
  update: (id: string, data: any) => api.put(`/projects/${id}`, data),
  /** Is this web address free? Advisory — the write is what actually claims it (#353). */
  checkSubdomain: (id: string, label: string) =>
    api.get(`/projects/${id}/subdomain/available`, { params: { label } }),
  /** Change the app's web address. Landing moves files; fullstack rebuilds (#353). */
  changeSubdomain: (id: string, subdomain: string) =>
    api.patch(`/projects/${id}/subdomain`, { subdomain }),

  // Custom domains — the customer's OWN domain attached to this project.
  listDomains: (id: string) => api.get(`/projects/${id}/domains`),
  attachDomain: (id: string, domain: string) =>
    api.post(`/projects/${id}/domains`, { domain }),
  verifyDomain: (id: string, domainId: string) =>
    api.post(`/projects/${id}/domains/${domainId}/verify`),
  refreshDomain: (id: string, domainId: string) =>
    api.post(`/projects/${id}/domains/${domainId}/refresh`),
  removeDomain: (id: string, domainId: string) =>
    api.delete(`/projects/${id}/domains/${domainId}`),
  // The main domain: the app is built for it and the foundingdev.com address
  // redirects there. false stops redirecting.
  setPrimaryDomain: (id: string, domainId: string, primary: boolean) =>
    api.post(`/projects/${id}/domains/${domainId}/primary`, { primary }),
  
  // Deleting archives a published app's data and destroys its machines before
  // answering: 20 prod deletes took 1.9-29.8 s, and one ran past the global
  // 30 s timeout, so the user saw "failed" for an app that was in fact deleted
  // (Sentry NODE-EXPRESS-G).
  delete: (id: string) => api.delete(`/projects/${id}`, { timeout: 180_000 }),
  
  sync: (id: string) => api.post(`/projects/${id}/sync`),
  
  getBranches: (id: string) => api.get(`/projects/${id}/branches`),
  
  // Git operations
  getGitStatus: (id: string) => api.get(`/projects/${id}/git/status`),
  
  getGitDiff: (id: string, baseBranch?: string) =>
    api.get(`/projects/${id}/git/diff`, { params: { baseBranch } }),

  getDevServer: (id: string) =>
    api.get<DevServerStatusResponse>(`/projects/${id}/dev-server`),

  // Boot-preview snapshot HTML (the cached static husk shown while a machine
  // reboots). Fetched authenticated (Bearer) then rendered via iframe srcdoc —
  // an iframe `src` can't carry the Authorization header. Returns raw HTML text.
  getSnapshot: (id: string) =>
    api.get<string>(`/projects/${id}/snapshot`, { responseType: 'text' }),

  // `branch` is the user-selected source branch for the upcoming task.
  // When supplied, the Fly machine boots on this branch so the first docker
  // image build matches the task's branch (no stale node_modules on first
  // task). Optional & backward-compatible — omitting it falls through to the
  // backend's default branch resolution.
  /** The owner archives an app to free a slot under the plan's cap; code and data are kept. 202 {queued} when the tar runs in the lifecycle queue (lifecycle.state 'archiving'). */
  archive: (id: string) => api.post<{ ok: boolean; archived?: boolean; queued?: boolean; lifecycle: any }>(`/projects/${id}/archive`),
  /** Brings an archived app back; 402 plan_required when the plan has no room for it; 202 {queued} when the redeploy runs in the lifecycle queue (lifecycle.state 'restoring'). */
  restore: (id: string) => api.post<{ ok: boolean; restored?: boolean; queued?: boolean; via?: string; lifecycle: any }>(`/projects/${id}/restore`),
  startDevServer: (id: string, opts?: { branch?: string }) =>
    api.post(`/projects/${id}/dev-server/start`, opts?.branch ? { branch: opts.branch } : {}),

  stopDevServer: (id: string) => api.post(`/projects/${id}/dev-server/stop`),

  setMode: (id: string, uiMode: 'builder' | 'owner') =>
    api.put(`/projects/${id}/mode`, { uiMode }),

  getEnvVars: (id: string) => api.get(`/projects/${id}/env`),

  updateEnvVars: (id: string, environmentVariables: Array<{ key: string; value: string; isSecret: boolean }>, loadedAt?: string) =>
    // The server waits up to 75 s while the running app picks the change up.
    api.put(`/projects/${id}/env`, { environmentVariables, ...(loadedAt ? { loadedAt } : {}) }, { timeout: 90_000 }),

  // Workspace dirty-state hint surfaced to the dashboard's new-task flow.
  // Cheap to call (single Mongo read) and never wakes the Fly Machine.
  getWorkspaceState: (id: string) => api.get(`/projects/${id}/workspace-state`),

  // Records the user's intent to discard unsaved changes from a previous
  // task. The reset+clean is applied lazily by the worker the next time a
  // task starts, so this returns immediately even when the machine is asleep.
  discardWorkspace: (id: string) => api.post(`/projects/${id}/workspace/discard`),

  // Statically-discovered routes from the project source. Used to seed the
  // preview URL bar dropdown so every route is jumpable by default.
  getRoutes: (id: string) => api.get(`/projects/${id}/routes`),
};

// Landing form entries (server/routes/projectForms.js). Values are visitor
// input: render them as text, never as HTML.
export type FormTab = 'inbox' | 'review' | 'spam' | 'preview';
export interface FormCounts { inbox: number; unread: number; review: number; reviewUnread: number; spam: number; preview: number; last: string | null }
export interface FormSummary {
  enabled: boolean;
  role: 'admin' | 'member';
  host: string | null;
  siteUrl: string | null;
  retentionMonths: 3 | 6 | 12 | 24 | null;
  legacy: boolean;
  forms: { name: string; label: string; fields: string[]; source: 'build' | 'runtime'; counts: FormCounts }[];
  totals: FormCounts;
  month: { count: number | null; cap: number | null; hidden: number };
}
export interface FormEntry {
  id: string;
  formName: string;
  fields: { name: string; value: string }[];
  sender: { name: string; email: string; label: string };
  summary: string;
  replyUrl: string | null;
  hidden: boolean;
  page: string;
  status: 'inbox' | 'suspect' | 'spam';
  suspectReason: string | null;
  test: boolean;
  source: 'site' | 'preview' | 'judge';
  readAt: string | null;
  createdAt: string;
}
export const formsAPI = {
  summary: (projectId: string) => api.get<FormSummary>(`/projects/${projectId}/forms`),
  list: (projectId: string, params: { tab: FormTab; form?: string; cursor?: string | null; limit?: number }) =>
    api.get<{ items: FormEntry[]; nextCursor: string | null }>(`/projects/${projectId}/forms/submissions`, {
      params: { tab: params.tab, ...(params.form ? { form: params.form } : {}), ...(params.cursor ? { cursor: params.cursor } : {}), ...(params.limit ? { limit: params.limit } : {}) },
    }),
  get: (projectId: string, id: string) => api.get<{ item: FormEntry }>(`/projects/${projectId}/forms/submissions/${id}`),
  markRead: (projectId: string, id: string) => api.post(`/projects/${projectId}/forms/submissions/${id}/read`),
  markAllRead: (projectId: string, tab: FormTab, form?: string) => api.post(`/projects/${projectId}/forms/read-all`, { tab, ...(form ? { form } : {}) }),
  setStatus: (projectId: string, id: string, status: 'inbox' | 'spam') =>
    api.patch<{ item: FormEntry }>(`/projects/${projectId}/forms/submissions/${id}`, { status }),
  remove: (projectId: string, id: string) => api.delete(`/projects/${projectId}/forms/submissions/${id}`),
  /** The CSV as a Blob (the API needs the auth header, so not a plain link). */
  exportCsv: (projectId: string, params: { form?: string; includeReview?: boolean }) =>
    api.get<Blob>(`/projects/${projectId}/forms/export.csv`, {
      params: { ...(params.form ? { form: params.form } : {}), ...(params.includeReview ? { includeReview: '1' } : {}) },
      responseType: 'blob',
      timeout: 120_000,
    }),
  retentionPreview: (projectId: string, months: number) =>
    api.get<{ months: number; count: number }>(`/projects/${projectId}/forms/retention-preview`, { params: { months } }),
  setRetention: (projectId: string, retentionMonths: 3 | 6 | 12 | 24 | null) =>
    api.patch<{ retentionMonths: number | null }>(`/projects/${projectId}/forms/settings`, { retentionMonths }),
};

// Tasks API
export const tasksAPI = {
  getAll: (params?: any) => api.get('/tasks', { params }),
  
  getByProject: (projectId: string, params?: any) => 
    api.get('/tasks', { params: { project: projectId, ...params } }),
  
  getById: (id: string) => api.get(`/tasks/${id}`),

  // Read-only source view (developer mode "Files" tab). These go through the
  // shared `api` instance on purpose: it carries the baseURL and the Bearer
  // token. A bare relative fetch('/api/...') hits the CRA dev server instead
  // and comes back as index.html with a 200, which looks like corrupt JSON.
  getCodeTree: (id: string) => api.get(`/tasks/${id}/code/tree`),

  getCodeFile: (id: string, path: string) =>
    api.get(`/tasks/${id}/code/file`, { params: { path } }),

  downloadCode: (id: string) =>
    api.get(`/tasks/${id}/code/download`, { responseType: 'blob' }),
  
  create: (data: { title: string; description?: string; projectId: string; branch: string; agent?: 'claude' | 'cursor'; images?: any[]; codexModel?: string; }) => api.post('/tasks', data),
  
  update: (id: string, data: any) => api.put(`/tasks/${id}`, data),
  
  delete: (id: string) => api.delete(`/tasks/${id}`),
  
  archive: (id: string) => api.post(`/tasks/${id}/archive`),
  
  cancel: (id: string) => api.post(`/tasks/${id}/cancel`),
  
  retry: (id: string) => api.post(`/tasks/${id}/retry`),
  
  getChatHistory: (id: string, params?: any) => api.get(`/tasks/${id}/chat`, { params }),
  
  getCommandLogs: (id:string, params?: any) => api.get(`/tasks/${id}/commands`, { params }),
  
  getTerminalOutput: (id: string, params?: any) => api.get(`/tasks/${id}/terminal`, { params }),
  
  // Git operations for tasks
  getTaskGitDiff: (taskId: string, baseBranch?: string, uncommittedOnly?: boolean) =>
    api.get(`/tasks/${taskId}/git/diff`, { params: { baseBranch, uncommittedOnly } }),

  // The same change set in plain English, for the owner who does not read
  // diffs. Separate from the diff on purpose: the file list must not wait on
  // an LLM call. Server-cached per change set, so this is usually one findOne.
  getTaskChangeSummary: (taskId: string) =>
    api.get(`/tasks/${taskId}/changes/summary`),

  createOrUpdatePullRequest: (taskId: string) =>
    api.post(`/tasks/${taskId}/pr`),

  // Save/deploy runs the pre-save type-check + lint gate (and may auto-fix
  // failures) inside the workspace container before pushing — that can take
  // minutes, well past the global 30s timeout. Give it a long per-request
  // timeout matching the EC2→daemon deploy timeout.
  // `publish: true` is the explicit first go-live for a fullstack app (stamps
  // prodPublishedAt on the server). After that, a normal save auto-deploys.
  // `tier` (only meaningful on the first publish) chooses always-on vs sleep.
  // `confirmCharge` is the customer's yes to a hosting charge (the confirm
  // sheet): without it the server refuses a publish that would charge.
  deploy: (taskId: string, publish: boolean = false, tier?: 'sleep' | 'always-on', confirmCharge?: import('../lib/billing').ChargeConfirmation | null) =>
    api.post(
      `/tasks/${taskId}/deploy`,
      publish ? { publish: true, ...(tier ? { tier } : {}), ...(confirmCharge ? { confirmCharge } : {}) } : undefined,
      // 20min: the deploy request stays open for the daemon's pre-save verify
      // (type-check+lint) + commit + push, which on a huge monorepo (Twenty)
      // exceeds 10min. Matches the server→daemon timeout (tasks.js). Fly's proxy
      // has no hard duration cap (60s idle only, handled by the daemon keepalive),
      // so this client cap is the real ceiling.
      { timeout: 1_200_000 },
    ),

  // Live one-click-deploy status for the Publish popover (prod URL + lifecycle
  // status + tier + last-deployed). Cheap; polled while a deploy is in flight.
  getProdStatus: (taskId: string) => api.get(`/tasks/${taskId}/prod-status`),

  // Unpublish / full teardown: deletes the live site (S3 for landing, Fly app +
  // volume + custom domain for fullstack) and frees the subdomain. Destructive +
  // one-way — the UI confirms first. Can take a bit (Fly destroy), so allow 2min.
  unpublish: (taskId: string) => api.post(`/tasks/${taskId}/unpublish`, undefined, { timeout: 120_000 }),

  revert: (taskId: string, sha: string) =>
    api.post(`/tasks/${taskId}/revert`, { sha }),

  getGitLog: (taskId: string, limit?: number) =>
    api.get(`/tasks/${taskId}/git/log`, { params: { limit } }),

  getExecutions: (taskId: string) => api.get(`/tasks/${taskId}/executions`),

  getDeploymentStatus: (taskId: string) => api.get(`/tasks/${taskId}/deployment-status`),

  startBrowserPreviewSession: (taskId: string, url: string) =>
    api.post(`/tasks/${taskId}/browser-preview/session`, { url }),

  resizeBrowserPreview: (taskId: string, width: number, height: number) =>
    api.post(`/tasks/${taskId}/browser-preview/resize`, { width, height }),

  navigateBrowserPreview: (taskId: string, url: string) =>
    api.post(`/tasks/${taskId}/browser-preview/navigate`, { url }),

  refreshBrowserPreview: (taskId: string) =>
    api.post(`/tasks/${taskId}/browser-preview/refresh`),

  backBrowserPreview: (taskId: string) =>
    api.post(`/tasks/${taskId}/browser-preview/back`),

  forwardBrowserPreview: (taskId: string) =>
    api.post(`/tasks/${taskId}/browser-preview/forward`),

  browserPreviewAction: (taskId: string, action: any) =>
    api.post(`/tasks/${taskId}/browser-preview/action`, action),

  stopBrowserPreviewSession: (taskId: string) =>
    api.delete(`/tasks/${taskId}/browser-preview/session`),

  // iframe-default preview: ask the server how to render this preview
  // (iframe vs playwright stream). source defaults to the Fly dev server.
  getPreviewMode: (taskId: string, source: 'dev' | 'prod' = 'dev') =>
    api.get(`/tasks/${taskId}/preview-mode`, { params: { source } }),

  // iframe-mode keep-warm ping (server is blind to in-frame interaction).
  previewHeartbeat: (taskId: string) =>
    api.post(`/tasks/${taskId}/preview-heartbeat`),

  // Approve a plan-mode chat message and kick off the build follow-up turn.
  // Server returns 202 { jobId, message } and creates an "Approved the plan"
  // command bubble; the build turn streams in normally over the socket.
  approvePlan: (taskId: string, messageId: string) =>
    api.post(`/tasks/${taskId}/chat/${messageId}/approve-plan`),
};

// @-ref autocomplete — previously lived on planModeClient. The endpoint is
// project-search, unrelated to plan mode itself; surfacing it on the projects
// API keeps the call site self-explanatory.
export async function searchRefs(q: string, excludeProjectId?: string, kind?: 'app') {
  const params: Record<string, string> = {};
  if (q) params.q = q;
  if (excludeProjectId) params.exclude = excludeProjectId;
  // 'app': apps only, never a video (what a video can show).
  if (kind) params.kind = kind;
  try {
    const res = await api.get('/projects/refs', { params });
    return Array.isArray(res.data) ? res.data : [];
  } catch {
    return [];
  }
}

// Scratch API — landing-from-scratch flow. UI surfaces it via the Dashboard
// composer when no project is selected (new-app mode).
export const scratchAPI = {
  // Creates the GitHub repo + Project + Task synchronously, fires the Fly
  // machine boot + initial agent command asynchronously, returns the task
  // id for redirect. Server responds 201 within ~5-10s; full boot + first
  // agent edit takes 60-90s after that.
  //
  // - mode: 'build' (default) = agent writes files directly.
  // - mode: 'plan' = agent proposes a plan via present_plan_to_user, user
  //   approves in chat, then a build pass materializes the plan.
  // - buildKind: 'landing' and 'fullstack' are both live. 'mobile' still
  //   returns 501 from the server (its UI tab stays visible-but-disabled so
  //   users see the roadmap). Each live tier routes to its own scratch
  //   service, machine image, system prompt, and build gate — see
  //   server/routes/scratch.js for the dispatch.
  create: (data: {
    // Required for the prompt-driven flow; omitted for a template import,
    // where the descriptor supplies everything the server needs.
    prompt?: string;
    projectName?: string;
    mode?: 'build' | 'plan';
    buildKind?: 'landing' | 'fullstack' | 'mobile' | 'video';
    // Video only (server lib/videoScratch.js parseOutput): the shape, length
    // and captions the composer's output control picked. Built by
    // components/video/videoApi.ts videoScratchBody.
    output?: {
      aspect?: '16:9' | '9:16' | '1:1' | '4:5' | 'auto';
      lengthSec?: number;
      captions?: 'burned' | 'off' | 'auto';
    };
    // @-cross-refs from the composer. Persisted on the initial TaskChat
    // message's payload.refs so the worker's existing fetchPlanRefs path
    // picks them up and clones each into /refs/<projectId>/ on the Fly
    // machine — same mechanism as imported-project task refs.
    refs?: Array<{ projectId: string; name?: string }>;
    // Doc attachments uploaded via the composer's unified paperclip
    // (server normalizes upload-shape → subdoc-shape; see
    // server/lib/normalizeAttachments.js).
    attachments?: any[];
    // Images attached in the new-app composer. Typed alongside attachments
    // because they take a DIFFERENT server path — docs are inlined as text,
    // images ride the SDK multimodal pipeline off the initial chat row. Absent
    // from this type until 2026-09-23, which is why the missing field at the
    // call site type-checked clean and shipped.
    images?: any[];
    // Template import — the gallery's "Use this template". When present the
    // server resolves the descriptor, takes buildKind FROM it (landing or
    // fullstack), and ignores whatever the client sent. prompt/projectName
    // become optional.
    //
    // NOTE this flow does NOT run an initial agent turn (no scaffolding pass),
    // so the 60-90s timing note above applies to the prompt-driven flow only.
    templateId?: string;
  }) =>
    // 5 minutes, NOT the 30s global. This call is synchronous through a GitHub
    // repo create + a clone-and-push of the template source, so its duration
    // scales with the template repo's size. A landing template finishes in
    // ~5-10s and never noticed the global ceiling; the first fullstack one
    // (WeKan, a Meteor tree with a large history) measured 39s and so failed
    // EVERY time — while succeeding server-side.
    //
    // That combination is the reason this is a per-request override rather
    // than a bigger global: an axios timeout rejects with no `response`, so
    // useTemplateImport's messageFor(undefined) shows the generic "give it
    // another go", and going again creates a SECOND repo, Project and Fly
    // machine for one user intent. A silent duplicate is much worse than a
    // slow spinner, so the client has to outwait the server rather than
    // guess that a slow call has died.
    api.post('/scratch', data, { timeout: 300_000 }),
};

// Template registry — backs the Templates gallery. Read-only; descriptors are
// code artifacts that ship with the server deploy and have to agree with what
// the fdev-landing-template image baked, so there is no write surface here.
/** One screen in a template's detail reel. `label` names the screen for the
 *  caption and the thumbnail's accessible name ("Bookings"); null falls back
 *  to a numbered alt. */
export interface TemplateScreenshot {
  src: string;
  label: string | null;
}

export interface TemplateSummary {
  slug: string;
  name: string;
  description: string;
  longDescription: string;
  category: string;
  /** What the template is FOR — "Spa & Wellness", "Plumbing", ... The axis a
   *  small-business owner actually chooses on. */
  vertical: string;
  /** Live deployed site. The MODAL iframes this so the user can scroll the
   *  real thing before committing. Cards do not — see thumbnailUrl. Null when
   *  `deployable` is false — there is no site to preview yet. */
  previewUrl: string | null;
  /** Same-origin card art. A landing card's is captured from previewUrl
   *  (scripts/capture-template-thumbs.mjs); a SaaS card's is designed cover
   *  art; every coming-soon card wears the shared
   *  /template-thumbs/coming-soon.png, which templateRegistry supplies. Cards
   *  use this rather than an iframe so the grid renders instantly and does
   *  not depend on five remote sites being up. Null only for a descriptor
   *  with no art at all. */
  thumbnailUrl: string | null;
  /** Screens for the detail view's reel, in display order — the first is the
   *  opening picture. Empty means the cover alone; the server always sends
   *  the array, optional here only so older fixtures keep type-checking. */
  screenshots?: TemplateScreenshot[];
  /** 1-3 first-edit suggestions, per template. Rendered as tappable chips on
   *  the seeded chat message — the only onboarding a template user gets.
   *  Empty when `deployable` is false (nothing gets imported). */
  starterChips: string[];
  /** Paid tools this descriptor stands in for (e.g. ["HubSpot", "Pipedrive"])
   *  — drives the "Replaces X" badge and brand-name search. Empty for a
   *  general-purpose template with no specific tool in mind. */
  replaces: string[];
  /** The honesty flag. False means there is no repo behind this descriptor
   *  yet, so there is nothing to provision — NEVER render "Use this
   *  template" or POST /api/scratch for it. `lib/templateCta.ts` is the one
   *  place that decides the CTA from this flag; go through it rather than
   *  branching on `deployable` again at a call site. */
  deployable: boolean;
  /** True (or implied by `deployable: false`) puts a "Coming soon" pill on
   *  the card. */
  comingSoon: boolean;
  /** The prompt the "Plan my replacement" CTA lands the user on instead of a
   *  dead "Use this template" button. Required by the server when
   *  `deployable` is false; null otherwise. */
  chatPrefill: string | null;
  /** Upstream open-source project this template's tree comes from, or null
   *  when we wrote it. Present so the UI can credit the real authors — the
   *  card's "by Founding Dev" badge would otherwise claim someone else's
   *  application as ours, which the licence forbids and the customer is
   *  poorly served by: this is the project whose docs and community apply to
   *  the app they now own. templateRegistry REQUIRES this on any deployable
   *  fullstack template. */
  basedOn: {
    name: string;
    /** Upstream repo or homepage. Always https. */
    url: string;
    /** SPDX-ish short name, e.g. "MIT", "AGPL-3.0". */
    license: string;
    /** The upstream copyright line, verbatim, when they publish one. */
    copyright: string | null;
  } | null;
  /** Whether an app started from this template can have its code downloaded
   *  (server templateRegistry codeDownloadOf): 'plan' = on a paid plan, like
   *  any app (open-source-based and landing templates); 'contact' = never,
   *  even after edits: Founding.dev built it in-house, so the customer talks
   *  to us for the code. Absent on an older server, which reads as 'plan'. */
  codeDownload?: 'plan' | 'contact';
}

export const templatesAPI = {
  list: () => api.get<{ templates: TemplateSummary[] }>('/templates'),
  get: (slug: string) => api.get<{ template: TemplateSummary }>(`/templates/${slug}`),
};

// GitHub API
export const githubAPI = {
  getUser: () => api.get('/github/user'),
  
  getRepos: (params?: any) => api.get('/github/repos', { params }),
  
  getRepo: (owner: string, repo: string) =>
    api.get(`/github/repos/${owner}/${repo}`),
  
  getBranches: (owner: string, repo: string) =>
    api.get(`/github/repos/${owner}/${repo}/branches`),
  
  getPulls: (owner: string, repo: string, params?: any) =>
    api.get(`/github/repos/${owner}/${repo}/pulls`, { params }),
  
  getPull: (owner: string, repo: string, pullNumber: number) =>
    api.get(`/github/repos/${owner}/${repo}/pulls/${pullNumber}`),
  
  createPull: (owner: string, repo: string, data: any) =>
    api.post(`/github/repos/${owner}/${repo}/pulls`, data),
  
  searchRepos: (params: any) => api.get('/github/search/repositories', { params }),
  
  getOrgs: () => api.get('/github/orgs'),
  
  createRepo: (data: { name: string; description?: string; private?: boolean }) =>
    api.post('/github/repos', data),
};

// Plan Sessions API (v2 plan mode — PLAN_SESSION_V2 feature flag).
// The session creation side runs through planModeClient.startPlanStream;
// this API surfaces the read + close lifecycle once a session exists.
export const planSessionsAPI = {
  get: (id: string) => api.get(`/plan-sessions/${id}`),
  close: (id: string) => api.post(`/plan-sessions/${id}/close`),
};

// Upload API
export const uploadAPI = {
  uploadImage: (formData: FormData, onUploadProgress?: (progressEvent: any) => void) =>
    api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    }),
};

// Connectors (third-party MCP integrations)
//
// Public shape of a registry entry (server strips internal fields):
export interface ConnectorRegistryEntry {
  id: string;
  displayName: string;
  brandIcon: string;
  tagline: string;
  authType: 'oauth' | 'apikey' | 'github_app';
  // 'per_tool' (default) — UI renders the per-tool always/ask/never grid.
  // 'all_or_nothing' — enabling the connector enables every tool; UI hides
  //   the per-tool grid and shows a single inline message instead. Used
  //   for universal-key connectors (server holds the API key) where
  //   per-tool toggles add noise without a security boundary.
  permissionMode?: 'per_tool' | 'all_or_nothing';
  // 'connect' — the customer's own account: Connect sends them to the
  //   service's consent page (bring-your-own: Stripe, Notion, Linear).
  // 'enable' — a platform-held key flipped on and off (HeyGen).
  // Absent on old servers → treated as 'enable'.
  connectMode?: ConnectorConnectMode;
  // Groups and orders the modal ('payments', 'docs', 'project'); null for
  // connectors without one.
  category?: string | null;
  // One line shown above Connect when the service's consent page needs a
  // hint (Atlassian: untick a product the site doesn't have).
  connectNote?: string;
  // Stripe: read-only until the account is known to be test mode (review P0).
  readOnlyUnlessTestMode?: boolean;
}

export type ConnectorConnectMode = 'connect' | 'enable';

// Every status a connections row can carry. Bring-your-own rows add
// 'needs_reauth' (reconnect) and 'needs_setup'. The `string & {}` arm keeps
// a status the client does not know yet type-safe: it renders generic copy
// (components/connectors/copy.ts), never a blank row (DX-17).
export type ConnectorStatus =
  | 'connected'
  | 'error'
  | 'disconnected'
  | 'enabled'
  | 'disabled'
  | 'not_enabled'
  | 'needs_reauth'
  | 'needs_setup'
  | (string & {});

export interface ConnectorConnection {
  connectorId: string;
  // Wave 2 of the HeyGen migration introduced 'enabled'/'disabled' for
  // universal-key connectors. The OAuth shape ('connected'/'error'/
  // 'disconnected') is preserved verbatim for any future OAuth connector
  // and for backward compat with stale rows in dev databases. 'not_enabled'
  // is what GET /connectors/:id/status returns when no row exists yet —
  // sometimes surfaces here when the connections endpoint is replaced
  // mid-flight by the status endpoint.
  status: ConnectorStatus;
  connectedAt: string | null;
  lastUsedAt: string | null;
  scopes: string[];
  // Bring-your-own rows only (absent or null for HeyGen).
  accountLabel?: string | null; // "Connected as <accountLabel>"
  accountMode?: 'test' | 'live' | null; // Stripe test/live account
  lastErrorCode?: string | null; // a connectorContract code
  readOnly?: boolean;
}

export type ConnectorPermissionState = 'always' | 'ask' | 'never';

export interface ConnectorToolDescriptor {
  name: string;
  description: string;
}

export interface ConnectorPermissionsResponse {
  connectorId: string;
  defaultPermission: ConnectorPermissionState;
  globalPermission: ConnectorPermissionState;
  toolPermissions: Record<string, ConnectorPermissionState>;
  readOnly?: boolean;
  tools: ConnectorToolDescriptor[];
}

// Wire shape of a BackgroundRenderJob — server emits this via
// .toWire() in server/models/BackgroundRenderJob.js. Keep in sync.
export interface RenderJobWire {
  _id: string;
  task: string;
  connectorId: string;
  kind: string;
  status: 'queued' | 'polling' | 'completed' | 'failed' | 'cancelled' | 'abandoned';
  progress: {
    phase: string | null;
    percent: number | null;
    stepsDone: number | null;
    stepsTotal: number | null;
    message: string | null;
  };
  result: {
    artifactId: string | null;
    assetUrl: string | null;
    assetKind: string | null;
    errorCode: string | null;
    errorMessage: string | null;
  };
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const renderJobsAPI = {
  list: (taskId: string, activeOnly = false) =>
    api.get<{ jobs: RenderJobWire[] }>(
      `/tasks/${taskId}/render-jobs${activeOnly ? '?active=true' : ''}`
    ),

  cancel: (taskId: string, jobId: string) =>
    api.post<{ job: RenderJobWire; alreadyTerminal?: boolean }>(
      `/tasks/${taskId}/render-jobs/${jobId}/cancel`
    ),
};

// Phase 5 — saved chat deliverables (durable, versioned markdown documents).
export type DeliverableVerificationStatus = 'verified' | 'flagged' | 'unverified' | 'skipped';

export interface DeliverableFlag {
  text: string;
  kind: 'number' | 'entity';
  reason?: string;
}

export interface DeliverableWire {
  id: string;
  title: string;
  content: string;
  format: 'markdown';
  presentationUrl?: string; // real themed .pptx (S3) for the office-viewer preview
  slideCount?: number; // number of slides (presentation deliverables)
  version: number;
  status: string;
  conversation: string | null;
  provenance: { authoredBy?: string; model?: string; createdVia?: string; sourceQuery?: string };
  dependsOn: Array<{ type: string; refId: string; name?: string }>;
  verification: { status: DeliverableVerificationStatus; flags: DeliverableFlag[]; criticSummary: string; checkedAt: string | null };
  flagCount: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface DeliverableListItem {
  id: string;
  title: string;
  format: string;
  verificationStatus: DeliverableVerificationStatus;
  conversation: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export const deliverablesAPI = {
  /** Save a chat assistant answer as a durable document. */
  create: (conversationId: string, messageId: string, title?: string) =>
    api.post<{ deliverable: DeliverableWire }>('/deliverables', { conversationId, messageId, title }),
  /** List the owner's saved documents (Documents section). */
  list: () => api.get<{ deliverables: DeliverableListItem[] }>('/deliverables'),
  /** Fetch one document (full content) for the viewer. */
  get: (id: string) => api.get<{ deliverable: DeliverableWire }>(`/deliverables/${id}`),
  /** Download a document rendered to docx/xlsx/pptx (server-side, auth-scoped). */
  export: (id: string, format: 'docx' | 'xlsx' | 'pptx' = 'docx') =>
    api.get(`/deliverables/${id}/export?format=${format}`, { responseType: 'blob' }),
};

// Universal-key (server-held API key) connector lifecycle. Parallel to
// the OAuth connect/disconnect flow but used for connectors where the
// server holds one API key and just records that a given owner has
// "enabled" the connector — no per-user OAuth. Wave 2 of the HeyGen
// migration; current single consumer is heygen.
export type ConnectorEnableStatus = 'not_enabled' | 'enabled' | 'disabled';

export interface ConnectorEnableResponse {
  ok: boolean;
  connectorId: string;
  status: ConnectorEnableStatus;
  enabledAt?: string | null;
  disabledAt?: string | null;
}

export interface ConnectorStatusResponse {
  connectorId: string;
  status: ConnectorEnableStatus;
  enabledAt: string | null;
  disabledAt: string | null;
}

// Shape of an error body returned by the universal-key routes. Server
// includes `userMessage` — a plain-English string safe to surface to a
// blue-collar non-technical audience. Never surface `error` (the code).
export interface ConnectorEnableErrorBody {
  error: string;
  userMessage: string;
}

// Bring-your-own connect flow (server/routes/connectors.js). Errors are
// RFC 9457 problem bodies; read them with components/connectors/connectorErrors.ts.
export interface ConnectorProblemBody {
  type?: string;
  title?: string;
  status?: number;
  code?: string;
  detail?: string;
  userMessage?: string;
  requestId?: string;
  authorizeUrl?: string; // 409 reauthorize only
}

export interface ConnectorConnectResponse {
  authorizeUrl?: string;
  connected?: boolean;
  accountLabel?: string | null;
  accountSwitched?: boolean;
}

export interface ConnectorCompleteBody {
  code?: string;
  state?: string;
  iss?: string;
  error?: string;
}

export interface ConnectorCompleteResponse {
  connectorId: string;
  result: 'connected' | 'cancelled' | 'error' | (string & {});
  returnTo: string;
  accountLabel?: string | null;
  accountSwitched?: boolean;
}

export const connectorsAPI = {
  registry: () =>
    api.get<{ connectors: ConnectorRegistryEntry[] }>('/connectors/registry'),

  connections: () =>
    api.get<{ connections: ConnectorConnection[] }>('/connectors/connections'),

  // HeyGen (connectMode 'enable') uses the universal-key enable/disable
  // flow below; bring-your-own connectors use connect/disconnectOAuth at
  // the bottom.

  // One-click enable/disable for universal-key connectors. Used by the
  // HeyGen connect button — no popup, no OAuth, just a flag flip.
  enable: (id: string) =>
    api.post<ConnectorEnableResponse>(`/connectors/${id}/enable`),

  disable: (id: string) =>
    api.post<ConnectorEnableResponse>(`/connectors/${id}/disable`),

  getStatus: (id: string) =>
    api.get<ConnectorStatusResponse>(`/connectors/${id}/status`),

  getPermissions: (id: string) =>
    api.get<ConnectorPermissionsResponse>(`/connectors/${id}/permissions`),

  updatePermissions: (
    id: string,
    body: { globalPermission: ConnectorPermissionState; toolPermissions: Record<string, ConnectorPermissionState> }
  ) => api.put<ConnectorPermissionsResponse>(`/connectors/${id}/permissions`, body),

  // ── Bring-your-own (connectMode 'connect') ──────────────────────────
  // The service's consent link for this user. The caller navigates the
  // whole page there (window.location.assign); the service sends the
  // browser back to /connectors/callback. returnTo is where to land after.
  // GitHub answers `connected` instead: it reuses the signed-in GitHub link.
  connect: (id: string, returnTo: string) =>
    api.post<ConnectorConnectResponse>(`/connectors/${id}/connect`, { returnTo }),

  // The callback page hands the service's answer to the server, once.
  completeOAuth: (body: ConnectorCompleteBody) =>
    api.post<ConnectorCompleteResponse>('/connectors/oauth/complete', body),

  disconnectOAuth: (id: string) =>
    api.post<{ disconnected: boolean; vendorRevoked: boolean }>(`/connectors/${id}/disconnect`),

  // PUT /permissions REPLACES globalPermission and toolPermissions (an
  // omitted field is reset, not kept), so read the current values first and
  // send them back unchanged with the new readOnly.
  // The server changes only the fields sent, so per-tool choices are untouched.
  setReadOnly: (id: string, readOnly: boolean) =>
    api.put<ConnectorPermissionsResponse>(`/connectors/${id}/permissions`, { readOnly }),
};

// ── Team collaboration (multiplayer) ─────────────────────────────────────────

// "Team" = Organization in the API. UI never says "organization".
export const teamsAPI = {
  list: () => api.get('/teams'),
  create: (name: string) => api.post('/teams', { name }),
  members: (orgId: string) => api.get(`/teams/${orgId}/members`),
  invite: (
    orgId: string,
    email: string,
    role: 'org-admin' | 'member' = 'member',
    opts: { appAccessMode?: 'all' | 'selected'; projectIds?: string[] } = {}
  ) => api.post(`/teams/${orgId}/invite`, { email, role, ...opts }),
  removeMember: (orgId: string, userId: string) =>
    api.delete(`/teams/${orgId}/members/${userId}`),
  revokeInvite: (orgId: string, invitationId: string) =>
    api.post(`/teams/${orgId}/invitations/${invitationId}/revoke`),
  rename: (orgId: string, name: string) => api.patch(`/teams/${orgId}`, { name }),
  // The team's apps — powers the AppAccessPicker (invite modal + per-member
  // access management on the Team page).
  apps: (orgId: string) => api.get(`/teams/${orgId}/apps`),
  setMemberAccessMode: (orgId: string, userId: string, mode: 'all' | 'selected') =>
    api.patch(`/teams/${orgId}/members/${userId}/access`, { mode }),
  grantApp: (orgId: string, userId: string, projectId: string) =>
    api.put(`/teams/${orgId}/members/${userId}/apps/${projectId}`),
  revokeApp: (orgId: string, userId: string, projectId: string) =>
    api.delete(`/teams/${orgId}/members/${userId}/apps/${projectId}`),
  // Apps the caller can manage in this team, with who has access. Powers SharedAppsCard.
  sharedApps: (orgId: string) => api.get(`/teams/${orgId}/shared-apps`),
};

// The invitee side of an invite (the person being invited).
export const invitationsAPI = {
  get: (token: string) => api.get(`/invitations/${token}`),
  accept: (token: string) => api.post(`/invitations/${token}/accept`),
  decline: (token: string) => api.post(`/invitations/${token}/decline`),
};

// App-level sharing ("People with access" + per-app invite).
export const appAccessAPI = {
  list: (projectId: string) => api.get(`/projects/${projectId}/access`),
  invite: (projectId: string, email: string, role: 'admin' | 'member' = 'member') =>
    api.post(`/projects/${projectId}/invite`, { email, role }),
  remove: (projectId: string, userId: string) =>
    api.delete(`/projects/${projectId}/access/${userId}`),
  // App activity feed (any member can view this app's history).
  activity: (projectId: string) => api.get(`/projects/${projectId}/activity`),
  // Invite autocomplete: org teammates (with hasAccess flag) for this app.
  orgMembers: (projectId: string) => api.get(`/projects/${projectId}/org-members`),
  // Cancel a pending app invite (app admin only).
  revokeInvite: (projectId: string, invitationId: string) =>
    api.post(`/projects/${projectId}/invitations/${invitationId}/revoke`),
};

// In-app notifications (invites, access changes, mentions). Backed by socket
// pushes on the `notification` event for live updates; these are the REST reads.
export const notificationsAPI = {
  list: () => api.get('/notifications'),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id: string) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
  archive: (id: string) => api.post(`/notifications/${id}/archive`),
  archiveAll: () => api.post('/notifications/archive-all'),
};

// Team-level activity feed (org-wide history).
export const teamActivityAPI = {
  activity: (orgId: string) => api.get(`/teams/${orgId}/activity`),
};

// General-AI chat (Dashboard surface — distinct from the per-app build flow).
export const conversationsAPI = {
  create: (title?: string) => api.post('/conversations', { title }),
  list: () => api.get('/conversations'),
  get: (id: string) => api.get(`/conversations/${id}`),
  // Backpressure (eng-review T2): if the instance is momentarily full the server
  // replies 429 {reason:'at_capacity', retryAfterMs}. Retry with backoff (bounded)
  // so the message is never silently lost — the same clientMsgId keeps it
  // idempotent server-side. 'owner_limit' (this user already has a turn in
  // flight) is surfaced to the caller, not retried.
  sendMessage: async (id: string, text: string, clientMsgId?: string, attachments?: any[], appRefs?: Array<{ projectId: string; name: string }>, _attempt = 0): Promise<any> => {
    // The user's IANA timezone (e.g. "America/New_York") so the assistant can
    // state the date/time in THEIR local time. IANA name (not an offset) so the
    // server gets DST right. Guarded — older browsers may lack the API.
    let tz: string | undefined;
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch { /* leave undefined → server falls back to UTC */ }
    try {
      return await api.post(`/conversations/${id}/message`, { text, clientMsgId, attachments, appRefs, tz });
    } catch (err: any) {
      const status = err && err.response && err.response.status;
      const data = (err && err.response && err.response.data) || {};
      if (status === 429 && data.reason === 'at_capacity' && _attempt < 4) {
        const wait = data.retryAfterMs || 500 * Math.pow(2, _attempt);
        await new Promise((r) => setTimeout(r, wait));
        return conversationsAPI.sendMessage(id, text, clientMsgId, attachments, appRefs, _attempt + 1);
      }
      throw err;
    }
  },
  // Edit-and-resubmit: supersede the edited message + everything after, then
  // send the edited text as a fresh user turn (reconciled by clientMsgId).
  editMessage: (id: string, editMessageId: string, text: string, clientMsgId?: string) => {
    let tz: string | undefined;
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch { /* UTC fallback */ }
    return api.post(`/conversations/${id}/message`, { editMessageId, text, clientMsgId, tz });
  },
  // Retry → regenerate-in-place: reuse the last user turn, replace its answer.
  regenerate: (id: string) => {
    let tz: string | undefined;
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch { /* server falls back to UTC */ }
    return api.post(`/conversations/${id}/message`, { regenerate: true, tz });
  },
  stop: (id: string, turnId: string) => api.post(`/conversations/${id}/stop`, { turnId }),
  // Answer an in-chat clarify card: persists the answer on the card + continues the
  // build without a verbose echo bubble. deferred=true when the user skipped.
  clarifyAnswer: (id: string, messageId: string, answers: any[], deferred?: boolean) => {
    let tz: string | undefined;
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch { /* UTC */ }
    return api.post(`/conversations/${id}/clarify-answer`, { messageId, answers, deferred: !!deferred, tz });
  },
  // Approve/deny a live app-action card. On approve the server runs the exact
  // frozen tool+args and continues the turn; remember=true means "don't ask again".
  toolApproval: (id: string, messageId: string, approvalId: string, decision: 'approve' | 'deny', remember?: boolean) => {
    let tz: string | undefined;
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch { /* UTC */ }
    return api.post(`/conversations/${id}/tool-approval`, { messageId, approvalId, decision, remember: !!remember, tz });
  },
  rename: (id: string, title: string) => api.patch(`/conversations/${id}`, { title }),
  remove: (id: string) => api.delete(`/conversations/${id}`),
};

// Cross-chat memory: what the assistant remembers about the user (view/delete)
// + the memory-on/off toggle. See server/routes/memory.js.
export type ImportFactType = 'user' | 'business';
export interface ImportCandidate { id: string; text: string; factType: ImportFactType }
export interface ImportStatus {
  status: 'pending' | 'ready' | 'error';
  stats?: { candidates: number; skipped: number; chunks: number; truncated: boolean } | null;
  error?: string | null;
  candidates: ImportCandidate[];
}
export const memoryAPI = {
  list: () => api.get('/memory'),
  remove: (id: string) => api.delete(`/memory/${encodeURIComponent(id)}`),
  getSettings: () => api.get('/memory/settings'),
  setEnabled: (enabled: boolean) => api.put('/memory/settings', { enabled }),
  // Import: bring your memory from Claude/ChatGPT/etc. Start → poll → commit selection.
  importStart: (text: string, factType: ImportFactType) =>
    api.post<{ jobId: string }>('/memory/import', { text, factType }),
  importStatus: (jobId: string) =>
    api.get<ImportStatus>(`/memory/import/${encodeURIComponent(jobId)}`),
  importCommit: (jobId: string, selectedIds: string[]) =>
    api.post<{ added: number }>('/memory/import/commit', { jobId, selectedIds }),
};

// App-MCP: wire the general chat to a customer's own app's live actions. Lives on
// the app page (owner-scoped). The plain-language layer uses get/setPermissions/
// disable/revalidate; the collapsed "advanced" setup layer uses save/test.
export type AppMcpPermission = 'always' | 'ask' | 'never';
export const appMcpAPI = {
  get: (appId: string) => api.get(`/app-mcp/connections/${appId}`),
  test: (appId: string, body: { mcpUrl?: string; token?: string }) =>
    api.post(`/app-mcp/connections/${appId}/test`, body),
  save: (body: {
    appId: string;
    mcpUrl?: string;
    token?: string;
    toolAllowlist?: string[];
    toolTemplates?: Record<string, string>;
    toolPermissions?: Record<string, AppMcpPermission>;
    globalPermission?: AppMcpPermission;
    skipTest?: boolean;
  }) => api.post('/app-mcp/connections', body),
  setPermissions: (
    appId: string,
    body: { globalPermission?: AppMcpPermission; toolPermissions?: Record<string, AppMcpPermission> },
  ) => api.patch(`/app-mcp/connections/${appId}/permissions`, body),
  disable: (appId: string) => api.post(`/app-mcp/connections/${appId}/disable`, {}),
  revalidate: (appId: string) => api.post(`/app-mcp/connections/${appId}/revalidate`, {}),
  remove: (appId: string) => api.delete(`/app-mcp/connections/${appId}`),
};

// Ops admin panel (staff-only — server enforces via auth + requireStaff).
// See server/routes/opsMoonlightAdmin.js for the response shapes.
/** The admin Dashboard's range, as the server's lib/dashboardRange reads it. */
export interface DashboardRangeParams {
  range?: '7d' | '30d' | '90d' | 'custom';
  from?: string;
  to?: string;
  internal?: '1';
  refresh?: '1';
}

export const opsAdminAPI = {
  listUsers: (params?: {
    page?: number;
    limit?: number;
    q?: string;
    status?: string;
    sort?: string;
    dir?: 'asc' | 'desc';
    intent?: string;
    apps?: string;
    access?: string;
    label?: string;
    tier?: string;
  }) => api.get('/ops-moonlight-admin/users', { params }),
  // Staff-only tags. Replaces the whole list; [] clears it.
  setLabels: (id: string, labels: string[]) =>
    api.put(`/ops-moonlight-admin/users/${id}/labels`, { labels }),
  grantAccess: (id: string, body: { expiresAt: string | null }) =>
    api.post(`/ops-moonlight-admin/users/${id}/grant-access`, body),
  revokeAccess: (id: string) => api.post(`/ops-moonlight-admin/users/${id}/revoke-access`),
  suspend: (id: string) => api.post(`/ops-moonlight-admin/users/${id}/suspend`),
  unsuspend: (id: string) => api.post(`/ops-moonlight-admin/users/${id}/unsuspend`),
  // One action for many people (at most 200 ids per call). People the action
  // doesn't fit come back `skipped` with a reason; the call itself succeeds.
  bulk: (body: {
    ids: string[];
    action: 'add_access' | 'add_credit' | 'revoke' | 'suspend' | 'unsuspend' | 'add_label' | 'remove_label';
    days?: number | null;
    // add_credit: to each person's personal org. batchId is one per run, the
    // same on every chunk, so a retry never pays twice (server caps the run).
    amountUsd?: number;
    expiresAt?: string;
    reason?: string;
    batchId?: string;
    label?: string;
  }) => api.post('/ops-moonlight-admin/users/bulk', body),
  // Every id the list's filters match, for "select all N matching". Capped
  // server-side; `capped` says when the cap cut it short.
  listUserIds: (params?: {
    q?: string;
    status?: string;
    intent?: string;
    apps?: string;
    access?: string;
    label?: string;
    tier?: string;
  }) => api.get('/ops-moonlight-admin/users/ids', { params }),
  listMachines: (params?: { refresh?: '1' }) =>
    api.get('/ops-moonlight-admin/machines', { params }),
  // The per-user drill-in: this user's apps, the Fly machine behind each, and
  // what it is costing. Reads the CACHED sweep, so it returns fast and may
  // answer `inventory.pending` before the first sweep lands.
  listUserApps: (id: string) => api.get(`/ops-moonlight-admin/users/${id}/apps`),
  // What the owner typed into this app's tasks, oldest first (newest 200).
  getProjectPrompts: (projectId: string) => api.get(`/ops-moonlight-admin/projects/${projectId}/prompts`),
  // One person's plan and credit as they see it (percent used / left, tier),
  // plus every grant on their personal org with what was used and why.
  getUserCredit: (id: string) => api.get(`/ops-moonlight-admin/users/${id}/credit`),
  // The admin Dashboard. Every call takes the page's range (components/ops/dashboardFilters apiRange:
  // range=7d|30d|90d|custom with from/to as Pacific YYYY-MM-DD, internal=1 to count internal accounts)
  // so the tabs agree; cached server-side per range, refresh=1 recomputes.
  // Usage and cost by month, totals, and what happened in the range vs the period before.
  getDashboard: (params?: DashboardRangeParams) => api.get('/ops-moonlight-admin/dashboard', { params }),
  // Day / week / month series across the range, with like-for-like period comparisons (Pacific time).
  getDashboardTrends: (params: DashboardRangeParams & { granularity: 'day' | 'week' | 'month' }) =>
    api.get('/ops-moonlight-admin/dashboard/trends', { params }),
  // People per tier now, the change since the range started, movement between tiers.
  getDashboardTiers: (params?: DashboardRangeParams) => api.get('/ops-moonlight-admin/dashboard/tiers', { params }),
  // Onboarding funnel for people who signed up in the range, by sign-up week, with who stopped where.
  getDashboardFunnel: (params?: DashboardRangeParams) => api.get('/ops-moonlight-admin/dashboard/funnel', { params }),
  // Who had build requests fail in the range, how many, and what the last one said.
  getDashboardFailures: (params?: DashboardRangeParams) => api.get('/ops-moonlight-admin/dashboard/failures', { params }),
  // Rule-based findings + who runs out of credit within 3 days (fixed windows; internal=1 counts internal accounts).
  getDashboardInsights: (params?: { refresh?: '1'; internal?: '1' }) => api.get('/ops-moonlight-admin/dashboard/insights', { params }),
  /** The machine-size picker: plain-words presets and every size Fly accepts, each priced per running hour. */
  listMachineSizes: () => api.get('/ops-moonlight-admin/machine-sizes'),
  /** Give one app its own machine size (size null = its normal size). applyNow restarts a running preview onto it. */
  setMachineSize: (projectId: string, body: { size: { cpu_kind: string; cpus: number; memory_mb: number } | null; reason: string; applyNow: boolean }) =>
    api.put(`/ops-moonlight-admin/projects/${projectId}/machine-size`, body),

  // Pricing gate (T10/T12/T13): promo codes, contract flag, manual grants.
  // POST /promo-codes answers the plaintext code ONCE; the list never has it.
  createPromoCode: (body: {
    kind: 'promo' | 'beta';
    amountUsd: number;
    maxRedemptions: number;
    expiresAt?: string | null;
    grantLifetimeDays?: number | null;
    note?: string;
    code?: string;
  }) => api.post('/ops-moonlight-admin/promo-codes', body),
  listPromoCodes: () => api.get('/ops-moonlight-admin/promo-codes'),
  setPromoActive: (id: string, active: boolean) =>
    api.post(`/ops-moonlight-admin/promo-codes/${id}/active`, { active }),
  getOrgCredit: (orgId: string) => api.get(`/ops-moonlight-admin/orgs/${orgId}`),
  setOrgUnmetered: (orgId: string, body: { unmetered: boolean; reason: string }) =>
    api.post(`/ops-moonlight-admin/orgs/${orgId}/unmetered`, body),
  grantOrgCredit: (orgId: string, body: { amountUsd: number; expiresAt: string; reason: string }) =>
    api.post(`/ops-moonlight-admin/orgs/${orgId}/grants`, body),
  // Signup invite links (?invite=<code>). See server/routes/opsInvites.js.
  listInvites: () => api.get<{ invites: SignupInviteRow[]; openSignup: boolean }>('/ops-moonlight-admin/invites'),
  createInvite: (body: {
    code: string;
    name?: string;
    label: string;
    grantsAccess?: boolean;
    accessDays?: number | null;
    credit?: { amountUsd: number; maxPeople: number; creditDays: number; offerEndsAt?: string | null; existingAccounts?: boolean };
  }) => api.post<{ invite: SignupInviteRow }>('/ops-moonlight-admin/invites', body),
  updateInvite: (
    id: string,
    body: Partial<Pick<SignupInviteRow, 'name' | 'label' | 'grantsAccess' | 'accessDays' | 'active'>> & {
      credit?: { maxPeople?: number; offerEndsAt?: string | null; existingAccounts?: boolean };
    }
  ) => api.patch<{ invite: SignupInviteRow }>(`/ops-moonlight-admin/invites/${id}`, body),
  // Badge referrals (staff only). See server/routes/opsReferrals.js.
  // Referral program (staff only). See server/routes/opsReferrals.js /program/*.
  referralOverview: (params: { campaign?: string; from?: string; to?: string } = {}) =>
    api.get<ReferralOverview>('/ops-moonlight-admin/referrals/program/overview', { params }),
  referralList: (params: { referrer?: string; invitee?: string; q?: string; campaign?: string; status?: 'approval' | 'flagged'; source?: 'link' | 'badge'; before?: string; limit?: number } = {}) =>
    api.get<{ referrals: ReferralProgramRow[]; nextBefore: string | null }>('/ops-moonlight-admin/referrals/program/list', { params }),
  referralTop: (params: { campaign?: string; from?: string; to?: string; limit?: number } = {}) =>
    api.get<{ referrers: TopReferrerRow[] }>('/ops-moonlight-admin/referrals/program/top', { params }),
  referralApprovals: () =>
    api.get<{ ready: ReferralProgramRow[]; upcoming: ReferralProgramRow[]; readyCount: number; upcomingCount: number }>('/ops-moonlight-admin/referrals/program/approvals'),
  decideReferralReward: (id: string, side: 'invitee' | 'referrer', decision: 'approve' | 'decline', note?: string) =>
    api.post<{ ok: true; outcome: string }>(`/ops-moonlight-admin/referrals/program/${id}/rewards/${side}/${decision}`, { note }),
  referralForUser: (id: string) =>
    api.get<{ referredBy: ReferralProgramRow | null; referred: ReferralProgramRow[]; nextBefore: string | null; referredCount: number; chainDepth: number }>(`/ops-moonlight-admin/referrals/program/users/${id}`),
  referralCampaigns: () =>
    api.get<{ liveId: string | null; campaigns: ReferralCampaignRow[] }>('/ops-moonlight-admin/referrals/program/campaigns'),
  createReferralCampaign: (body: ReferralCampaignInput) =>
    api.post<{ campaign: ReferralCampaignRow }>('/ops-moonlight-admin/referrals/program/campaigns', body),
  updateReferralCampaign: (id: string, body: ReferralCampaignInput) =>
    api.patch<{ campaign: ReferralCampaignRow }>(`/ops-moonlight-admin/referrals/program/campaigns/${id}`, body),
  deleteReferralCampaign: (id: string) =>
    api.delete<{ deleted: string }>(`/ops-moonlight-admin/referrals/program/campaigns/${id}`),
};

// ── Referral program types ────────────────────────────────────────────────
// Server: lib/referrals.js (the user's view), lib/referralAdmin.js (admin).

export type ReferralMoment = 'signup' | 'publish' | 'subscribe';
export type ReferralRewardStatus = 'waiting' | 'granted' | 'declined' | 'capped' | 'ineligible';
export type SubscribeMode = 'fixed' | 'plan_month';

export interface ReferralRewardRow {
  moment: ReferralMoment;
  side: 'invitee' | 'referrer';
  amountUsd: number;
  status: ReferralRewardStatus;
  readyAt: string | null;
  grantedAt: string | null;
  decidedAt: string | null;
  note: string | null;
}

export interface ReferralProgramRow {
  id: string;
  referrer: ReferralPerson;
  invitee: ReferralPerson;
  source: 'link' | 'badge';
  code: string | null;
  badge: { subdomain: string | null; page: string | null; project: { id: string; name: string | null } | null; site: ReferralSite | null } | null;
  campaign: string | null;
  signedUpAt: string;
  eligibleAt: string | null;
  ineligibleReason: string | null;
  publishedAt: string | null;
  subscribedAt: string | null;
  subscription: { planId: string | null; refundedAt: string | null; disputedAt: string | null } | null;
  flags: string[];
  rewards: ReferralRewardRow[];
}

export interface ReferralOverview {
  funnel: {
    signedUp: number; eligible: number; published: number; subscribed: number;
    viaLink: number; viaBadge: number; flagged: number; referrers: number;
    publishRate: number | null; subscribeRate: number | null; friendsPerReferrer: number | null;
  };
  credit: { grantedUsd: number; grantedToReferrersUsd: number; grantedToInviteesUsd: number; waitingUsd: number; declinedUsd: number; cappedUsd: number };
  awaitingApproval: number;
  costPerPayingUsd: number | null;
  comparison: { allSignups: number; referredShare: number | null; referredPaidRate: number | null; otherPaidRate: number | null } | null;
}

export interface TopReferrerRow {
  referrer: ReferralPerson;
  signedUp: number;
  published: number;
  subscribed: number;
  viaBadge: number;
  earnedUsd: number;
}

interface RewardPair { inviteeUsd: number; referrerUsd: number }

export interface ReferralCampaignRow {
  id: string;
  name: string;
  isStandard: boolean;
  startsAt: string | null;
  endsAt: string | null;
  paused: boolean;
  rewards: { signup: RewardPair; publish: RewardPair; subscribe: RewardPair & { inviteeMode: SubscribeMode; referrerMode: SubscribeMode } };
  redemptionDays: number;
  creditLifetimeDays: number;
  monthlyCap: number;
  updatedAt: string | null;
  stats: { signedUp: number; published: number; subscribed: number };
}

export type ReferralCampaignInput = Partial<Omit<ReferralCampaignRow, 'id' | 'isStandard' | 'updatedAt' | 'stats'>>;

/** A referring site at its current address (follows a rename). */
export interface ReferralSite { url: string; label: string }

export interface MyReferrals {
  code: string;
  offer: {
    paused: boolean;
    signup: RewardPair;
    publish: RewardPair;
    subscribe: RewardPair & { inviteeMode: SubscribeMode; referrerMode: SubscribeMode };
    redemptionDays: number;
    creditLifetimeDays: number;
    monthlyCap: number;
  };
  counts: { signedUp: number; published: number; subscribed: number };
  credit: { earnedUsd: number; pendingUsd: number };
  cap: { used: number; limit: number };
  rows: {
    joinedAt: string;
    stage: 'signed_up' | 'published' | 'subscribed';
    source: 'link' | 'badge';
    /** The badge's site and page as recorded at the click (plain text). */
    page: string | null;
    /** That page at its current address, to link to; null once the site is gone. */
    site: ReferralSite | null;
    earnedUsd: number;
    pendingUsd: number;
    pendingFrom: string | null;
    notEligible: boolean;
    overCap: boolean;
  }[];
  nextBefore: string | null;
}

// The signed-in user's own referral link and progress. server/routes/referrals.js.
export const referralsAPI = {
  me: () => api.get<MyReferrals>('/referrals/me'),
  /** The next page of anonymous rows; `before` is the previous page's nextBefore. */
  /** Tell analytics the link was copied or shared (once a day per person, server-side). */
  shared: (how: 'copy' | 'share') => api.post('/referrals/me/shared', { how }),
  rows: (before: string) => api.get<{ rows: MyReferrals['rows']; nextBefore: string | null }>('/referrals/me/rows', { params: { before } }),
};

// Pricing gate (T5/T10/T11/T13). The org is always resolved server-side from
// the open project (or the caller's personal org); the client never names one.
// Shapes: server/routes/billing.js, billingRedeem.js, billingPauseCards.js;
// client types in lib/billing.ts.
export const billingAPI = {
  balance: (projectId?: string | null, orgId?: string | null) =>
    api.get('/billing/balance', { params: projectId ? { projectId } : orgId ? { orgId } : undefined }),
  /** Teams whose billing this user can manage (org-admin), personal first. */
  orgs: () => api.get<{ orgs: { orgId: string; orgName: string; isPersonal: boolean }[] }>('/billing/orgs'),
  /** `source`: where in the app the checkout started (plans_page, pause_card, …) — a label for the billing events, never money. */
  checkout: (body: { projectId?: string | null; orgId?: string | null; planId?: string | null; returnTo?: string | null; cancelTo?: string | null; source?: string | null }) =>
    api.post<{ url: string; sessionId: string; value?: number | null; planId?: string | null }>('/billing/checkout', body).then((r) => {
      // Browser half of the checkout conversion (Google Ads / GA4 / Meta pixel).
      trackBeginCheckout({ kind: 'plan', sessionId: r.data && r.data.sessionId, value: r.data && r.data.value, planId: (r.data && r.data.planId) || body.planId });
      return r;
    }),
  /** The tab came back from Stripe without a success: report the session it left for. Emits checkout_cancelled once; never changes billing. */
  checkoutCancelled: (sessionId: string) =>
    api.post<{ ok: boolean; cancelled: boolean; status: string | null }>('/billing/checkout/cancelled', { sessionId }),
  fulfill: (sessionId: string) =>
    api.post<import('../hooks/useCheckoutReturn').FulfillResult>(
      '/billing/checkout/fulfill',
      { sessionId },
    ),
  /** Switch an active subscription to another catalog plan (prorated; server T10). */
  /** Switch plans in place (T10, decision 12): prorated; `keepProjectIds` names what stays active on a downgrade over the cap. */
  changePlan: (body: { projectId?: string | null; orgId?: string | null; planId: string; keepProjectIds?: string[]; prorationDate?: number }) =>
    api.post<{ ok: boolean; planId: string; archived?: number }>('/billing/change-plan', body),
  /** Remove ONE free hosting slot of `tier`: no refund, it stops renewing and stays usable until the period ends. 409 = none free. */
  releaseHostingSlot: (body: { projectId?: string | null; orgId?: string | null; tier: 'sleep' | 'always-on' }) =>
    api.post<{ ok: boolean; tier: string; hosting: import('../lib/billing').Hosting }>('/billing/hosting/release', body),
  /** What publishing (or switching) this app on `tier` charges now: {charge:false} or the confirm sheet's numbers. 503 = Stripe could not price it. */
  hostingPreview: (params: { projectId: string; tier: 'sleep' | 'always-on' }) =>
    api.get<import('../lib/billing').HostingChargePreview>('/billing/hosting/preview', { params }),
  /** What a downgrade would archive (the picker's data): the org's active apps, the target's cap, the recommended keep set. */
  changePlanPreview: (params: { projectId?: string | null; orgId?: string | null; planId: string }) =>
    api.get<import('../lib/billing').ChangePlanPreview>('/billing/change-plan/preview', { params }),
  /** One-time top-up Checkout (P-TOPUP): whole dollars, $10 minimum; `requestId` makes a double click share one session. */
  topup: (body: { amountUsd: number; projectId?: string | null; orgId?: string | null; returnTo?: string | null; cancelTo?: string | null; requestId?: string | null; source?: string | null }) =>
    api.post<{ url: string; sessionId: string }>('/billing/topup', body).then((r) => {
      trackBeginCheckout({ kind: 'topup', sessionId: r.data && r.data.sessionId, value: body.amountUsd });
      return r;
    }),
  portal: (body: { projectId?: string | null; orgId?: string | null; returnTo?: string | null }) =>
    api.post<{ url: string }>('/billing/portal', body),
  redeem: (code: string, projectId?: string | null, orgId?: string | null) =>
    api.post<{ ok: boolean; addedMicros?: number; alreadyRedeemed?: boolean; covered?: boolean }>(
      '/billing/redeem',
      projectId ? { code, projectId } : orgId ? { code, orgId } : { code },
    ),
  /** The 24-hour template trial's card (trial plan only): Checkout in setup mode, the card is checked and never charged. Comes back to `returnTo` with ?session_id. */
  templateTrialCard: (body: { returnTo?: string | null; cancelTo?: string | null; source?: string | null }) =>
    api.post<{ url: string; sessionId: string }>('/billing/template-trial/card', body),
  pauseCard: (taskId: string) => api.get('/billing/pause-card', { params: { taskId } }),
  pauseCardCheckout: (cardId: string) => api.post(`/billing/pause-card/${cardId}/checkout`),
  pauseCardCancelCheckout: (cardId: string) => api.post(`/billing/pause-card/${cardId}/cancel-checkout`),
};

export interface SignupInviteRow {
  id: string;
  code: string;
  name: string;
  label: string;
  grantsAccess: boolean;
  accessDays: number | null;
  active: boolean;
  redemptions: number;
  lastRedeemedAt?: string | null;
  createdAt: string;
  /** What the link gives (its promo code), or null for a label-only link. */
  credit: InviteCredit | null;
}

export interface InviteCredit {
  amountUsd: number;
  maxPeople: number;
  used: number;
  offerEndsAt: string | null;
  creditDays: number | null;
  active: boolean;
  existingAccounts: boolean;
}

export interface ReferralPerson {
  id: string;
  name: string | null;
  email: string | null;
}

/** One user a customer's badge brought in. `subdomain` / `page` came from a
 *  URL anyone can type: show them as text, never as a link or HTML. */
export default api;
